import React, { useEffect, useRef } from 'react';
import { Link } from 'react-scroll';
import { FaArrowDown, FaStar, FaShoppingBag } from 'react-icons/fa';
import { GiKnifeFork, GiMeat } from 'react-icons/gi';
import { useCart } from '../context/CartContext';
import './Hero.css';

const Hero = () => {
  const canvasRef = useRef(null);
  const { openCart } = useCart();

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    let animId;

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resize();
    window.addEventListener('resize', resize);

    // Particle system (floating embers)
    const particles = Array.from({ length: 80 }, () => ({
      x: Math.random() * window.innerWidth,
      y: Math.random() * window.innerHeight,
      size: Math.random() * 2.5 + 0.5,
      speedX: (Math.random() - 0.5) * 0.4,
      speedY: -Math.random() * 0.8 - 0.2,
      opacity: Math.random() * 0.6 + 0.1,
      color: Math.random() > 0.5 ? '#c9a84c' : '#e8a030',
      life: Math.random(),
    }));

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      particles.forEach((p) => {
        p.x += p.speedX;
        p.y += p.speedY;
        p.life -= 0.003;
        if (p.life <= 0 || p.y < -10) {
          p.x = Math.random() * canvas.width;
          p.y = canvas.height + 10;
          p.life = 1;
          p.opacity = Math.random() * 0.6 + 0.1;
        }
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        ctx.fillStyle = p.color;
        ctx.globalAlpha = p.opacity * p.life;
        ctx.fill();
        ctx.globalAlpha = 1;
      });
      animId = requestAnimationFrame(animate);
    };
    animate();

    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener('resize', resize);
    };
  }, []);

  return (
    <section id="home" className="hero">
      {/* Canvas ember particles */}
      <canvas ref={canvasRef} className="hero-canvas" />

      {/* Layered backgrounds */}
      <div className="hero-bg-layer hero-bg-1" />
      <div className="hero-bg-layer hero-bg-2" />
      <div className="hero-bg-layer hero-bg-3" />
      <div className="hero-bg-layer hero-bg-grill" />

      {/* Overlay */}
      <div className="hero-overlay" />

      {/* Content */}
      <div className="hero-content">
        {/* Top badge */}
        <div className="hero-badge">
          <FaStar className="badge-star" />
          <span>Athlone's Favourite Takeaway</span>
          <FaStar className="badge-star" />
        </div>

        {/* Script tag */}
        <p className="hero-script">Welcome to</p>

        {/* Main heading */}
        <h1 className="hero-title">
          <span className="hero-title-top">King</span>
          <span className="hero-title-main">Kebab</span>
        </h1>

        {/* Subtitle */}
        <div className="hero-divider">
          <div className="hero-divider-line" />
          <GiMeat className="hero-divider-icon" />
          <span className="hero-subtitle-text">Takeaway &amp; Delivery</span>
          <GiMeat className="hero-divider-icon" />
          <div className="hero-divider-line" />
        </div>

        {/* Description */}
        <p className="hero-desc">
          Authentic kebabs, flame-grilled burgers, stone-baked pizzas &amp; rich Indian curries —
          crafted fresh and delivered straight to your door.
        </p>

        {/* Stats */}
        <div className="hero-stats">
          <div className="hero-stat">
            <span className="stat-num">5★</span>
            <span className="stat-label">Top Rated</span>
          </div>
          <div className="hero-stat-divider" />
          <div className="hero-stat">
            <span className="stat-num">€8</span>
            <span className="stat-label">From / Takeaway</span>
          </div>
          <div className="hero-stat-divider" />
          <div className="hero-stat">
            <span className="stat-num">50+</span>
            <span className="stat-label">Menu Items</span>
          </div>
        </div>

        {/* CTAs */}
        <div className="hero-ctas">
          <button className="btn-primary" onClick={openCart} id="hero-order-btn">
            <FaShoppingBag />
            <span>Order Takeaway</span>
          </button>
          <Link to="menu" smooth duration={700} offset={-80} className="btn-outline">
            <GiKnifeFork />
            <span>View Menu</span>
          </Link>
        </div>
      </div>

      {/* Scroll indicator */}
      <Link to="about" smooth duration={700} offset={-80} className="hero-scroll-indicator">
        <span>Scroll Down</span>
        <div className="scroll-arrow">
          <FaArrowDown />
        </div>
      </Link>

      {/* Bottom gradient */}
      <div className="hero-bottom-fade" />
    </section>
  );
};

export default Hero;
